import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations } from 'drizzle-orm';
import {
  boolean,
  doublePrecision,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { collarTypes } from './collar-types';
import { fabricTypes } from './fabric-types';
import { printTypes } from './print-types';
import { sales } from './sales';
import { shirtSizes } from './shirt-sizes';
import { shirtTypes } from './shirt-types';
import { sleeveTypes } from './sleeve-types';

export const saleItems = pgTable('sale_items', {
  id: uuid('id')
    .primaryKey()
    .$defaultFn(() => Bun.randomUUIDv7()),
  quantity: doublePrecision('quantity').notNull(),
  // ids de additional_options (scope 'item') selecionados nesse item — substitui as
  // antigas colunas fixas own_label/with_pocket, permitindo novas opções sem migration.
  selectedOptions: jsonb('selected_options').$type<string[]>().notNull().default([]),
  shirtTypePrice: doublePrecision('shirt_type_price').notNull(),
  sleeveTypePrice: doublePrecision('sleeve_type_price').notNull(),
  collarTypePrice: doublePrecision('collar_type_price').notNull(),
  fabricTypePrice: doublePrecision('fabric_type_price').notNull(),
  printTypePrice: doublePrecision('print_type_price').notNull(),
  unitaryPrice: doublePrecision('unitary_price').notNull(),
  totalPrice: doublePrecision('total_price').notNull(),
  saleId: uuid('sale_id')
    .references(() => sales.id, { onDelete: 'cascade' })
    .notNull(),
  shirtTypeId: uuid('shirt_type_id')
    .references(() => shirtTypes.id)
    .notNull(),
  sleeveTypeId: uuid('sleeve_type_id')
    .references(() => sleeveTypes.id)
    .notNull(),
  collarTypeId: uuid('collar_type_id')
    .references(() => collarTypes.id)
    .notNull(),
  fabricTypeId: uuid('fabric_type_id')
    .references(() => fabricTypes.id)
    .notNull(),
  printTypeId: uuid('print_type_id')
    .references(() => printTypes.id)
    .notNull(),
  // Nulo quando o tamanho ainda não foi informado no orçamento, ou quando o item tem mais
  // de um tamanho (nesse caso o detalhamento fica em `sizeBreakdown` — este campo vira só
  // uma conveniência pro caso comum de 1 tamanho só, evitando reescrever toda tela que já
  // lê `item.shirtSize`).
  shirtSizeId: uuid('shirt_size_id').references(() => shirtSizes.id),
  // Detalhamento por tamanho — [{ shirtSizeId, quantity }]. Soma das quantidades deve bater
  // com `quantity` do item quando completo; vazio = "tamanho ainda não informado".
  sizeBreakdown: jsonb('size_breakdown')
    .$type<{ shirtSizeId: string; label: string; quantity: number }[]>()
    .notNull()
    .default([]),
  artworkPath: text('artwork_path'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at')
    .notNull()
    .$defaultFn(() => new Date()),
  updatedAt: timestamp('updated_at')
    .$onUpdate(() => new Date())
    .notNull(),
});

export const saleItemsRelations = relations(saleItems, ({ one }) => ({
  sale: one(sales, {
    fields: [saleItems.saleId],
    references: [sales.id],
  }),
  shirtType: one(shirtTypes, {
    fields: [saleItems.shirtTypeId],
    references: [shirtTypes.id],
  }),
  sleeveType: one(sleeveTypes, {
    fields: [saleItems.sleeveTypeId],
    references: [sleeveTypes.id],
  }),
  collarType: one(collarTypes, {
    fields: [saleItems.collarTypeId],
    references: [collarTypes.id],
  }),
  fabricType: one(fabricTypes, {
    fields: [saleItems.fabricTypeId],
    references: [fabricTypes.id],
  }),
  printType: one(printTypes, {
    fields: [saleItems.printTypeId],
    references: [printTypes.id],
  }),
  shirtSize: one(shirtSizes, {
    fields: [saleItems.shirtSizeId],
    references: [shirtSizes.id],
  }),
}));

export type SaleItem = InferSelectModel<typeof saleItems>;
export type CreateSaleItemInput = InferInsertModel<typeof saleItems>;
export type UpdateSaleItemInput = Partial<CreateSaleItemInput>;
